import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

export default function Login() {
  const [companyCode, setCompanyCode] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [rememberUsername, setRememberUsername] = useState(false);
  const [showCompanyCodeInput, setShowCompanyCodeInput] = useState(false);
  const { login } = useAuth();

  useEffect(() => {
    const savedUsername = localStorage.getItem('savedUsername');
    const savedCompanyCode = localStorage.getItem('companyCode');
    
    if (savedUsername) {
      setUsername(savedUsername);
      setRememberUsername(true);
    }
    
    if (savedCompanyCode) {
      setCompanyCode(savedCompanyCode);
    } else {
      setShowCompanyCodeInput(true);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Save company code
      if (companyCode) {
        localStorage.setItem('companyCode', companyCode);
      }
      
      // Save or remove username based on checkbox
      if (rememberUsername) {
        localStorage.setItem('savedUsername', username);
      } else {
        localStorage.removeItem('savedUsername');
      }
      
      await login(companyCode, username, password);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-500 to-blue-700">
      <div className="bg-white p-8 rounded-2xl shadow-2xl w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800">WalkRound</h1>
          <p className="text-gray-600 mt-2">Management Portal</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {showCompanyCodeInput ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company Code
              </label>
              <input
                type="text"
                value={companyCode}
                onChange={(e) => setCompanyCode(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter 5-digit code"
                maxLength={5}
                required
              />
            </div>
          ) : (
            <div className="bg-gray-100 px-4 py-3 rounded-lg flex justify-between items-center">
              <span className="text-gray-700 font-medium">Company: {companyCode}</span>
              <button
                type="button"
                onClick={() => setShowCompanyCodeInput(true)}
                className="text-blue-600 hover:underline text-sm font-medium"
              >
                Change
              </button>
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Username
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Your username"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="••••••••"
              required
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="rememberUsername"
              checked={rememberUsername}
              onChange={(e) => setRememberUsername(e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="rememberUsername" className="ml-2 text-sm text-gray-700">
              Remember my username
            </label>
          </div>

          {error && (
            <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>
      </div>
    </div>
  );
}
